import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, KeyRound, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { resetPassword } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-navy-light/40 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md" data-testid="reset-password-page">
        <Link to="/" className="inline-flex items-center gap-2 text-gold-light hover:text-gold text-sm mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="h-14 w-14 rounded-full bg-navy flex items-center justify-center text-gold">
              <KeyRound className="h-6 w-6" />
            </div>
            <h1 className="mt-4 font-serif text-2xl font-bold text-navy">Reset Password</h1>
            <p className="text-sm text-gray-500 mt-1">Choose a new password for your account.</p>
          </div>

          {!token ? (
            <div className="text-center text-sm text-red-600" data-testid="reset-no-token">
              This reset link is missing its token. Please request a new password reset.
            </div>
          ) : done ? (
            <div className="flex flex-col items-center text-center" data-testid="reset-success">
              <CheckCircle2 className="h-14 w-14 text-green-500" />
              <p className="mt-3 text-gray-600">Your password has been reset.</p>
              <Button
                onClick={() => navigate("/crm/login")}
                className="mt-5 w-full bg-navy hover:bg-navy-light text-white font-semibold h-11"
                data-testid="reset-go-login-button"
              >
                Continue to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password (min. 6 characters)"
                  className="pl-10"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  className="pl-10"
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-navy hover:bg-navy-light text-white font-semibold h-11"
                data-testid="reset-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
